import { useDispatch } from "react-redux";

import { IgButton, IgPaper } from "@intelligems/ig-theme";
import {
  AnalyzeTinybirdResourceResponseBodyType,
  ExperienceFe,
} from "@intelligems/ig-types";
import Grid from "@mui/material/Grid";
import { AnalyticsSummaryBase } from "@src/features/ai/components/usages/AnalyticsSummaryBase";
import { useAnalyticsSummary } from "@src/features/ai/components/usages/useAnalyticsSummary";
import { Sparkle } from "@src/images/Icons/Sparkle";
import { setAiChat } from "@src/store/slices/global";
import { UseQueryResult } from "@tanstack/react-query";

interface AnalyticsSummaryProps {
  experience: ExperienceFe;
  dashboardData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
}

export const AnalyticsSummary = ({
  experience,
  dashboardData,
}: AnalyticsSummaryProps) => {
  const dispatch = useDispatch();
  const summaryData = useAnalyticsSummary({ experience, dashboardData });

  const onStartChat = () => {
    if (!summaryData.chatInitialContext) {
      return;
    }

    dispatch(
      setAiChat([
        { key: "open", value: true },
        {
          key: "initialContext",
          value: summaryData.chatInitialContext,
        },
        { key: "mode", value: "analytics" },
        { key: "start", value: true },
      ])
    );
  };

  if (!summaryData.hasValidAuth) {
    return null;
  }

  return (
    <Grid>
      <IgPaper sx={{ padding: "24px 24px 21px 24px" }}>
        <AnalyticsSummaryBase
          title={summaryData.title || "Results Summary"}
          summary={summaryData.summaryText}
          isLoading={summaryData.isLoading}
          timestamp={summaryData.timestamp}
          formatTimestamp={summaryData.formatTimestamp}
          onCopy={summaryData.handleCopyToClipboard}
          copyFeedback={summaryData.copyFeedback}
          onRefresh={summaryData.handleRefresh}
          refreshDisabled={summaryData.refreshDisabled}
          refreshTooltip={summaryData.refreshTooltip}
          footerSessionId={summaryData.sessionId}
          footerFeature="analytics"
          footerFeedbackTarget={summaryData.footerFeedbackTarget}
          footerWorkflowId={summaryData.footerWorkflowId}
          titleVariant="h2"
          titleWidth={300}
          headerSlot={
            <Grid>
              <Sparkle />
            </Grid>
          }
          actionSlot={
            <Grid>
              <IgButton
                variant={"outlined"}
                disabled={summaryData.startChatDisabled}
                onClick={onStartChat}
              >
                Chat about your results
              </IgButton>
            </Grid>
          }
        />
      </IgPaper>
    </Grid>
  );
};
